import React from 'react';

import {assert, assertAndContinue} from 'Asserts';
import {SystemColumnName} from 'data-frames/DataFrameConstants';
import {DataFrame, DataType, DeprecatedDataFrame} from 'data-frames/DataFrameTypes';
import resolveDataFrame from 'data-frames/util/resolveDataFrame';
import {
  AnalyticsVisualizationAggregationType as AggregationType,
  AnalyticsVisualizationErrorBarType as ErrorBarType,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import useMaskedBlockViewState from 'insights/analytics-queries/block-view-state/useMaskedBlockViewState';
import useRegressionBlockViewState from 'insights/analytics-queries/block-view-state/useRegressionBlockViewState';
import {AnalyticsVisualization} from 'insights/analytics-visualizations/__generated/AnalyticsVisualization';
import {VISUALIZATION_RESOLUTION_CONFIG} from 'insights/analytics-visualizations/AnalyticsVisualizationConstants';
import {ScatterPlotParameters} from 'insights/analytics-visualizations/AnalyticsVisualizationTypes';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import ScatterPlot, {ScatterPlotData, ScatterPlotProps} from 'ui-library/charting/ScatterPlot';
import {RegressionAnalysis} from 'ui-library/charting/statistics/regression/RegressionTypes';
import {
  AnalysisStatus,
  areAnalysesSuccessful,
  SuccessfulAnalysis,
} from 'ui-library/charting/statistics/Types';
import {CartesianPoint} from 'ui-library/charting/Types';
import LoadingOverlay from 'ui-library/LoadingOverlay';
import {omit} from 'util/omitKey';

import ChartErrorMessage from './ChartErrorMessage';
import addRegressionAnalysisToScatterPlotProps from './prop-converters/addRegressionAnalysisToScatterPlotProps';
import aggregateScatterPlotProps from './prop-converters/aggregateScatterPlotProps';
import convertScatterPlotProps, {InsightsScatterPlotPoint} from './prop-converters/convertScatterPlotProps';
import {SuccessfulConversionOutput} from './prop-converters/PropConverterTypes';

type OutlierConfig = NonNullable<
  ExtractByTypename<
    AnalyticsVisualization['parameters'],
    'AnalyticsVisualizationScatterPlotParametersNode'
  >['outlierConfig']
>;

const DUMMY_SCATTER_PLOT_DATA_FRAME: DeprecatedDataFrame<InsightsScatterPlotPoint> = {
  axisTypes: {
    [SystemColumnName.ROW_ID]: DataType.STRING,
    x: DataType.NUMBER,
    y: DataType.NUMBER,
  },
  points: [],
};

// TODO(BNCH-24546): Delete this converter function when no longer needed.
export function convertScatterPlotDataToDataFrame<DataPoint extends CartesianPoint>(
  data: ScatterPlotData<DataPoint>,
  datasetIndex: number
): DeprecatedDataFrame<DataPoint> {
  return {
    axisTypes: data.axisTypes,
    points: data.datasets[datasetIndex].points,
  };
}

// TODO(BNCH-24546): Delete this converter function when no longer needed.
export function convertDataFrameToScatterPlotData<DataPoint extends CartesianPoint>(
  dataFrame: DeprecatedDataFrame<DataPoint>
): ScatterPlotData<DataPoint> {
  const {axisTypes, points} = dataFrame;

  return {
    axisTypes,
    datasets: [{points}],
  } as ScatterPlotData<DataPoint>;
}

interface PresentationalScatterPlotViewerProps extends ScatterPlotProps<
  CartesianPoint & {[SystemColumnName.ROW_ID]: JSONScalar}
> {
  aggregationType: AggregationType | null;
  errorBarType: ErrorBarType | null;
  regressionAnalyses: Array<RegressionAnalysis> | null;
  outlierConfig?: OutlierConfig | null;
}

export function PresentationalScatterPlotViewer({
  aggregationType,
  errorBarType,
  outlierConfig,
  regressionAnalyses,
  ...scatterPlotProps
}: PresentationalScatterPlotViewerProps): JSX.Element {
  const aggregatedScatterPlotProps = aggregateScatterPlotProps(scatterPlotProps, {
    aggregationType,
    errorBarType,
    outlierConfig: outlierConfig ?? null,
  });

  if (regressionAnalyses == null || regressionAnalyses.length === 0) {
    return <ScatterPlot {...aggregatedScatterPlotProps} />;
  }

  const maybeErrorAnalysis = regressionAnalyses.find((analysis) => analysis.status === AnalysisStatus.ERROR);

  if (maybeErrorAnalysis != null) {
    return (
      <ChartErrorMessage message={maybeErrorAnalysis.message}>
        <ScatterPlot {...aggregatedScatterPlotProps} />
      </ChartErrorMessage>
    );
  } else if (regressionAnalyses.some((analysis) => analysis.status === AnalysisStatus.LOADING)) {
    return (
      <LoadingOverlay message="Calculating curve...">
        <ScatterPlot {...omit(aggregatedScatterPlotProps, 'onClickPoint')} />
      </LoadingOverlay>
    );
  } else {
    assert(areAnalysesSuccessful(regressionAnalyses), 'Unexpected unsuccessful regression analyses');
    const regressionScatterPlotProps = addRegressionAnalysisToScatterPlotProps(
      scatterPlotProps,
      regressionAnalyses,
      outlierConfig
    );

    // Reaggregate points after running regression to incorporate outliers and so regression is still run on original points
    const aggregatedScatterPlotProps = aggregateScatterPlotProps(regressionScatterPlotProps, {
      aggregationType,
      errorBarType,
      outlierConfig: outlierConfig ?? null,
    });

    return <ScatterPlot {...aggregatedScatterPlotProps} />;
  }
}

interface ScatterPlotViewerProps {
  conversionOutput: SuccessfulConversionOutput<ScatterPlotProps<InsightsScatterPlotPoint>>;
  parameters: ScatterPlotParameters;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;
}

/** @package */
export function DynamicRegressionCalculationScatterPlotViewer({
  conversionOutput: {
    props: {data, config},
  },
  parameters: {
    aggregationType,
    errorBarType,
    outlierConfig,
    regressionConfig,
    rowIdColumnName,
    seriesColumnName,
  },
}: ScatterPlotViewerProps): JSX.Element {
  // The code is this module assumes that there will only every be 1 partition in the data when masking or
  // regression is enabled. From a user perspective, we don't allow users to enabled either masking or regression
  // while the series column is set.
  if (seriesColumnName != null) {
    assertAndContinue(
      regressionConfig == null && rowIdColumnName == null,
      'Expected both regression and point masking to be disabled when series column is set.'
    );
  }
  // We can't conditionally execute hooks, but since we ignore all their outputs when regression and masking
  // is off, we can just call the hooks with an dummy data frame.
  const analysisInputDataFrame =
    seriesColumnName == null ? convertScatterPlotDataToDataFrame(data, 0) : DUMMY_SCATTER_PLOT_DATA_FRAME;

  const maskedBlockViewStateOutput = useMaskedBlockViewState({
    inputDataFrame: analysisInputDataFrame,
  });

  const {maskedDataFrame} = maskedBlockViewStateOutput;

  const maskedScatterPlotData = maskedBlockViewStateOutput.isMaskingEnabled
    ? convertDataFrameToScatterPlotData(maskedDataFrame)
    : data;

  const {regressionAnalysis} = useRegressionBlockViewState({
    inputDataset: maskedDataFrame,
    outlierConfig,
    regressionConfig,
  });

  return (
    <PresentationalScatterPlotViewer
      config={config}
      aggregationType={aggregationType}
      errorBarType={errorBarType}
      outlierConfig={outlierConfig}
      regressionAnalyses={regressionAnalysis == null ? null : [regressionAnalysis]}
      data={maskedScatterPlotData}
      // Disable masking if points are aggregated, since aggregated points are not part of the original dataset
      onClickPoint={
        maskedBlockViewStateOutput.isMaskingEnabled && aggregationType == null
          ? maskedBlockViewStateOutput.onToggleRowMask
          : undefined
      }
    />
  );
}

export default function ErrorHandlingScatterPlotViewer({
  dataFrame,
  parameters,
  regressionAnalyses,
}: {
  dataFrame: DataFrame;
  parameters: ScatterPlotParameters;
  regressionAnalyses?: Array<SuccessfulAnalysis<RegressionAnalysis>>;
}): JSX.Element {
  const resolvedDataFrame = resolveDataFrame(dataFrame, VISUALIZATION_RESOLUTION_CONFIG);
  const conversionOutput = convertScatterPlotProps(resolvedDataFrame, parameters);

  return conversionOutput.valid ? (
    regressionAnalyses != null ? (
      <PresentationalScatterPlotViewer
        {...conversionOutput.props}
        {...parameters}
        regressionAnalyses={regressionAnalyses}
      />
    ) : (
      <DynamicRegressionCalculationScatterPlotViewer
        conversionOutput={conversionOutput}
        parameters={parameters}
      />
    )
  ) : (
    <InlineBanner bannerType={InlineBannerType.ERROR} text={conversionOutput.message} />
  );
}
